import { fetchProfile, refreshTokens } from '@api';
import { useApplicationStore } from '@data';
import { tracking } from '@lib';
import { getAndSetFeatureFlags } from '@lib/firebase';
import { replace } from 'react-router-dom';

type Params = {
  request: Request;
};

export default async function authValidateTokenLoader({
  request,
}: Params): Promise<Response | void> {
  const urlParams = parseAuthTokensAndLocationFromRequest(request);

  const {
    auth,
    patient,
    resetState,
    devTools: { patientWebUrl },
  } = useApplicationStore.getState();

  function redirectToPatientApp() {
    window.location.href = patientWebUrl;
    return;
  }

  if (!urlParams) {
    return redirectToPatientApp();
  }

  resetState();
  await getAndSetFeatureFlags();
  const { setAccessToken } = auth;
  const { setPatientProfile } = patient;

  try {
    const { accessToken } = await refreshTokens(urlParams);

    setAccessToken(accessToken);
    const profile = await fetchProfile();

    tracking.identifyUser({ userId: profile.patientId, properties: {} });
    setPatientProfile(profile);
    // remove leading slash from urlParams.location if there is one and add an initial slash so this works whether the
    // url has a leading slash or not

    return replace(`/${urlParams.location.replace(/^\//, '')}`);
  } catch {
    return redirectToPatientApp();
  }
}

function parseAuthTokensAndLocationFromRequest(request: Request) {
  const url = new URL(request.url);
  const accessToken = url.searchParams.get('accessToken');
  const location = decodeURIComponent(
    url.searchParams.get('redirectTo') || 'id-verification',
  );

  if (!accessToken) {
    return undefined;
  }

  // Forward non-auth query params to the redirect destination
  const forwardParams = new URLSearchParams();
  for (const [key, value] of url.searchParams.entries()) {
    if (key !== 'accessToken' && key !== 'redirectTo') {
      forwardParams.set(key, value);
    }
  }
  const queryString = forwardParams.toString();
  const fullLocation = queryString ? `${location}?${queryString}` : location;

  return { accessToken, location: fullLocation };
}
